import { asArray, asRecord, firstText } from "@/lib/tina/value-readers";
import type { ArtistResolution, HitlContext } from "@/types/tina";

export interface LinkedMember {
  name: string;
  artistId?: string;
  matchType?: string;
}

export interface SpotifyCandidate {
  spotifyId: string;
  name: string;
  genres: string[];
  imageUrl?: string;
  dbExists: boolean;
  dbArtistId?: string;
}

export interface FlaggedMember {
  name: string;
  flag?: string;
  candidates: SpotifyCandidate[];
}

export type MemberAction = "pick" | "spotify" | "unmatched" | "drop";

export interface ArtistOption {
  id?: string;
  name: string;
  media?: { photos?: Array<{ url: string }> };
  stats?: { spotify?: { monthlyListeners?: number } };
}

export interface MemberDecision {
  action: MemberAction;
  artistId?: string;
  /** The full picked option, kept so RemoteSelect can render the selection. */
  pickedArtist?: ArtistOption;
  spotifyRef?: string;
}

export function readLinkedMembers(ctx: HitlContext): LinkedMember[] {
  const problem = asRecord(ctx.problem);
  return asArray(problem.resolved_artists).flatMap((entry) => {
    const record = asRecord(entry);
    const name = firstText(record.name, asRecord(record.artist).name);
    if (!name) return [];
    return [
      {
        name,
        artistId: firstText(
          record.artist_id,
          record.artistId,
          asRecord(record.artist).id,
        ),
        matchType: firstText(record.match_type, record.matchType),
      },
    ];
  });
}

function readSpotifyCandidates(
  record: Record<string, unknown>,
): SpotifyCandidate[] {
  return asArray(record.spotify_candidates).flatMap((entry) => {
    const c = asRecord(entry);
    const spotifyId = firstText(c.spotify_id);
    const name = firstText(c.name);
    if (!spotifyId || !name) return [];
    return [
      {
        spotifyId,
        name,
        genres: asArray(c.genres)
          .map((g) => firstText(g))
          .filter((g): g is string => Boolean(g)),
        imageUrl: firstText(c.image_url) || undefined,
        dbExists: c.db_exists === true,
        dbArtistId: firstText(c.db_artist_id) || undefined,
      },
    ];
  });
}

export function readFlaggedMembers(ctx: HitlContext): FlaggedMember[] {
  const problem = asRecord(ctx.problem);
  const linkedNames = new Set(readLinkedMembers(ctx).map((m) => m.name));
  return asArray(problem.per_artist_audit).flatMap((entry) => {
    const record = asRecord(entry);
    const name = firstText(record.name);
    if (!name || linkedNames.has(name)) return [];
    const flag = firstText(asRecord(record.verdict).flag);
    return [{ name, flag, candidates: readSpotifyCandidates(record) }];
  });
}

export function buildArtistResolutionPayload(
  decisions: Record<string, MemberDecision>,
): ArtistResolution {
  return {
    kind: "artist",
    members: Object.entries(decisions).map(([name, decision]) => {
      const member: ArtistResolution["members"][number] = {
        name,
        action: decision.action,
      };
      if (decision.action === "pick" && decision.artistId)
        member.artist_id = decision.artistId;
      if (decision.action === "spotify" && decision.spotifyRef)
        member.spotify_ref = decision.spotifyRef;
      return member;
    }),
  };
}

// Mirror of tina's src/shared/spotify_ref.py — the worker re-run parses the
// pasted ref the same way and fails the stage non-retryably (stranding the
// event) when it doesn't resolve, so reject a bad paste here at submit time.
const SPOTIFY_BARE_ID = /^[A-Za-z0-9]{22}$/;
const SPOTIFY_URI_ARTIST = /^spotify:artist:([A-Za-z0-9]+)$/;
const SPOTIFY_URL_ARTIST =
  /open\.spotify\.com\/(?:intl-[a-z]{2}\/)?artist\/([A-Za-z0-9]+)/;

/** Return the bare Spotify artist id from a link/URI/id, or null if not an artist ref. */
export function parseSpotifyArtistId(
  raw: string | null | undefined,
): string | null {
  const text = (raw ?? "").trim();
  if (!text) return null;
  if (SPOTIFY_BARE_ID.test(text)) return text;
  const uri = SPOTIFY_URI_ARTIST.exec(text);
  if (uri) return uri[1];
  const url = SPOTIFY_URL_ARTIST.exec(text);
  if (url) return url[1];
  return null;
}

export function isMemberDecisionComplete(
  decision: MemberDecision | undefined,
): boolean {
  if (!decision) return false;
  if (decision.action === "pick") return Boolean(decision.artistId);
  if (decision.action === "spotify")
    return parseSpotifyArtistId(decision.spotifyRef) !== null;
  return true; // unmatched | drop
}

export function allFlaggedResolved(
  flagged: FlaggedMember[],
  decisions: Record<string, MemberDecision>,
): boolean {
  return flagged.every((m) => isMemberDecisionComplete(decisions[m.name]));
}
